import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils.ts'

const iconButtonStyles = cva(
  'inline-flex items-center justify-center rounded-md transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed',
  {
    variants: {
      variant: {
        primary: 'bg-brand-700 text-white hover:bg-brand-800 focus-visible:ring-brand-500',
        secondary:
          'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 focus-visible:ring-gray-400',
        ghost: 'text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus-visible:ring-gray-400',
        danger: 'text-red-600 hover:bg-red-50 focus-visible:ring-red-400',
      },
      size: {
        xs: 'h-6 w-6 [&>svg]:h-3.5 [&>svg]:w-3.5',
        sm: 'h-8 w-8 [&>svg]:h-4 [&>svg]:w-4',
        md: 'h-9 w-9 [&>svg]:h-4 [&>svg]:w-4',
        lg: 'h-10 w-10 [&>svg]:h-5 [&>svg]:w-5',
      },
    },
    defaultVariants: {
      variant: 'ghost',
      size: 'md',
    },
  },
)

export interface IconButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof iconButtonStyles> {
  icon: ReactNode
  /** Required for accessibility */
  'aria-label'?: string
  /** Alias for aria-label */
  label?: string
  loading?: boolean
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ className, variant, size, loading, disabled, icon, label, type = 'button', 'aria-label': ariaLabel, ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-label={ariaLabel ?? label}
      className={cn(iconButtonStyles({ variant, size }), className)}
      {...props}
    >
      {loading ? <Loader2 className="animate-spin" /> : icon}
    </button>
  ),
)
IconButton.displayName = 'IconButton'
